import { Drawer, Flex, Text } from '@servicetitan/anvil2';

interface NoticeDrawerProps {
    onClose: () => void;
}

export const ElectronicRecordsDisclosureDrawer = ({ onClose }: NoticeDrawerProps) => {
    return (
        <Drawer open onClose={onClose}>
            <Drawer.Header>FEDERAL ESIGN DISCLOSURE AND CONSENT</Drawer.Header>
            <Drawer.Content>
                <Flex direction="column" gap="4">
                    <Text>
                        Please read this Federal ESIGN Disclosure carefully and indicate you consent
                        below.
                    </Text>
                    <Text>
                        The following disclosures are required to be provided to you by your dealer
                        (or the dealer’s service provider, Service Finance Company, LLC) under the
                        federal Electronic Signatures in Global and National Commerce Act (“ESIGN”).
                        They are necessary in order for you to provide your consent to receive and
                        sign the Retail Installment Sale Contract or Promissory Note (depending on
                        your state of residence), any Addendum or Amendment thereto (collectively,
                        the “Retail Installment Sale Contract”), the Consumer Loan Application and
                        related disclosures as further described below electronically. Electronic
                        Delivery of Disclosures and Notices.
                    </Text>
                    <Text>
                        By clicking in the box below, you are consenting to receive the Retail
                        Installment Sale Contract, the Consumer Loan Application and all
                        disclosures, notices, terms and conditions, and other related documents and
                        any changes to the foregoing (collectively, the “Contract Documents”)
                        electronically. Your consent will apply to the dealer,{' '}
                        <strong>Service Finance Company, LLC</strong> (
                        <strong>the “Company”</strong>), and to any subsequent assignee(s) of the
                        Contract Documents. In order to access, receive and retain the Contract
                        Documents, you must satisfy the minimum system requirements outlined below.
                        We reserve the right, in our sole discretion, to provide you with any of the
                        Contract Documents in paper form instead of, or in addition to, electronic
                        form. Paper Delivery of Disclosures and Notices.
                    </Text>
                    <Text>
                        You are not required to provide your consent in order to proceed with the
                        Retail Installment Sale Contract. If you prefer not to provide your consent,
                        all of the Contract Documents will be executed and provided to you in paper
                        form. If you decide to provide your consent, you still have the right to
                        receive a paper copy of the Contract Documents. To receive a paper copy at
                        no charge, please request it in one of the following ways: send an e-mail
                        message with your name and mailing address to{' '}
                        <a href="mailto:info@svcfin.com">info@svcfin.com</a>; call us at{' '}
                        <strong>(866) 254-0497</strong>; fax us at <strong>(866) 257-8122</strong>;
                        or write to us at Service Finance Company, LLC at 555 South Federal Highway,
                        Suite 200, Boca Raton, Florida 33432 with your name and mailing address. Be
                        sure to state that you are requesting a copy of the Contract Documents.
                    </Text>
                    <Text>
                        System Requirements to Access Information. To receive an electronic copy of
                        the Contract Documents, you must have the following equipment and software:
                    </Text>
                    <ul>
                        <li>
                            A personal computer or other device which is capable of accessing the
                            Internet. Your access to this page verifies that your system/device
                            meets these requirements.
                        </li>
                        <li>
                            An Internet web browser which is capable of supporting 128-bit SSL
                            encrypted communications, which requires a minimum web browser version
                            of either Microsoft® Internet Explorer version 6.0 (available for
                            downloading at{' '}
                            <a
                                href="http://www.microsoft.com/windows/ie/downloads/default.asp"
                                title="http://www.microsoft.com/windows/ie/downloads/default.asp"
                                target="_blank"
                                rel="noreferrer"
                            >
                                Microsoft
                            </a>
                            ) and your system or device must have 128-bit SSL encryption software.
                            Your access to this page verifies that your browser and encryption
                            software/device meet these requirements.
                        </li>
                        <li>
                            You must have software which permits you to receive and access Portable
                            Document Format or “PDF” files, such as Adobe® Acrobat Reader® version
                            8.0 and above (download at{' '}
                            <a
                                href="http://www.adobe.com/products/acrobat/readstep2.html"
                                title="http://www.adobe.com/products/acrobat/readstep2.html"
                                target="_blank"
                                rel="noreferrer"
                            >
                                Adobe
                            </a>
                            ). These ESIGN disclosures and the Contract Documents are both provided
                            to you in PDF; therefore, your access to this page from your own
                            system/device verifies that your system/device has the necessary
                            software to permit you to receive and access PDF files.
                        </li>
                        <li>
                            A valid email address or, if you do not have a valid email address, you
                            can use your valid user credentials to access a secure webpage at{' '}
                            <a
                                href="https://e-sign.svcfin.com"
                                title="https://e-sign.svcfin.com"
                                target="_blank"
                                rel="noreferrer"
                            >
                                e-sign
                            </a>
                            .
                        </li>
                    </ul>
                    <strong>System Requirements to Retain Information</strong>
                    <Text>
                        If you have a valid email address, a copy of the Contract Documents will be
                        provided to you upon execution by email in the form of a PDF attachment. A
                        copy of the Contract Documents also will be made available to you upon
                        execution by your accessing a secure webpage at{' '}
                        <a
                            href="https://e-sign.svcfin.com"
                            title="https://e-sign.svcfin.com"
                            target="_blank"
                            rel="noreferrer"
                        >
                            e-sign
                        </a>{' '}
                        and logging in using your individual access credentials. To retain a copy of
                        the Contract Documents, you may choose to print them, save them or both. In
                        order to print them, you must have a functioning printer connected to your
                        personal computing device. If you wish to retain an electronic version of
                        the Contract Documents, you must have a personal computing device with the
                        hardware and software necessary to download and store PDF files.
                    </Text>
                    <Text variant="headline" size="small" el="h3">
                        Withdrawal of Electronic Acceptance of Disclosures and Notices
                    </Text>
                    <Text>
                        You can also contact us in any of the ways described above to withdraw your
                        consent to receive any future disclosures, notices, terms and conditions,
                        and other documents related to the Contract Documents electronically, if
                        any. There are no fees for withdrawal of your consent. If you withdraw your
                        consent, we will send all future Contract Documents (if any) to you in paper
                        form. If you decide to withdraw your consent, the withdrawal will not affect
                        the legal effectiveness, validity, and enforceability of prior electronic
                        Contract Documents. Your withdrawal of your consent will be effective within
                        a reasonable period of time following our receipt thereof to allow for time
                        to process your request.
                    </Text>
                    <strong>Changing your Email Address</strong>
                    <Text>
                        It is important that you keep your email address current with us. Therefore,
                        please notify us immediately at the addresses and/or telephone numbers set
                        forth above if you change your email address. We may treat your failure to
                        provide us with a valid email address, or the malfunction of the email
                        address we have for you, as a withdrawal of your consent to receive Contract
                        Documents electronically.
                    </Text>
                    <Text variant="headline" size="small" el="h3">
                        Right to Discontinue Electronic Provision of Contract Documents
                    </Text>
                    <Text>
                        We reserve the right, in our sole discretion, to discontinue electronic
                        provision of Contract Documents. We will provide you with notice of any such
                        termination as required by applicable law. If we discontinue electronic
                        provision of Contract Documents, we will send all future Contract Documents
                        (if any) to you in paper form.
                    </Text>
                    <Text variant="headline" size="small" el="h3">
                        Systems Enhancements
                    </Text>
                    <Text>
                        If the system requirements for accessing the copy of your Contract Documents
                        on the secure webpage change so as to require different or additional
                        hardware or software, or upgrades, plug-ins or enhancements to the hardware
                        or software used by you (all such changes referred to as a “Systems
                        Enhancement”), we will notify you and you may either withdraw your consent
                        or upgrade your hardware and/or software as required by the Systems
                        Enhancement. If we determine that the need for a Systems Enhancement creates
                        a material risk that you may not be able to access or retain the Contract
                        Documents electronically, we will notify you and allow you to either: (a)
                        withdraw your consent or (b) re-confirm your consent to receive the Contract
                        Documents in a manner that reasonably demonstrates that you have upgraded
                        your hardware or software to conform to the required Systems Enhancement. If
                        you choose to withdraw your consent, or you fail to re-confirm your consent
                        (which we will treat as your withdrawal of consent), you will no longer
                        receive Contract Documents electronically and we will send all future
                        Contract Documents to you in paper form.
                    </Text>
                </Flex>
            </Drawer.Content>
        </Drawer>
    );
};
export const GeneralApplicationDisclosuresDrawer = ({ onClose }: NoticeDrawerProps) => {
    return (
        <Drawer open onClose={onClose}>
            <Drawer.Header>General Application Disclosures</Drawer.Header>
            <Drawer.Content>
                <Flex direction="column" gap="4">
                    <Text>
                        NOTICE TO CALIFORNIA RESIDENTS: A married applicant may apply for a separate
                        account. After credit approval, each applicant shall have the right to use
                        this account to the extent of any credit limit set by the creditor and each
                        applicant may be liable for all amounts of credit extended under this
                        account to each joint applicant.
                    </Text>
                    <Text>
                        NOTICE TO NEW YORK AND RHODE ISLAND RESIDENTS: A consumer report may be
                        ordered on you in connection with your application for credit. If you ask,
                        we will tell you whether or not one was ordered and if one was, the name and
                        address of the consumer reporting agency that provided it. Subsequent
                        consumer reports may be requested or used in connection with an update,
                        renewal or extension of the credit applied for without further notice to
                        you.
                    </Text>
                    <Text>
                        NOTICE TO OHIO RESIDENTS: The Ohio laws against discrimination require that
                        all creditors make credit equally available to all creditworthy customers,
                        and that credit reporting agencies maintain separate credit histories on
                        each individual upon request. The Ohio Civil Rights Commission administers
                        compliance with this law.
                    </Text>
                    <Text>
                        NOTICE TO VERMONT RESIDENTS: You authorize us to obtain a consumer credit
                        report from one or more consumer credit reporting agencies in connection
                        with your application and as otherwise allowed by law. You agree that we may
                        also verify your employment, income, assets, and debts. If your application
                        is approved and the contract is entered into, you also authorize us, and our
                        employees and agents, to obtain additional credit reports and other
                        information about you in connection with updates, renewals, extensions,
                        account review, collections activity, or for any other legitimate purpose.
                    </Text>
                    <Text>
                        NOTICE TO MARRIED WISCONSIN RESIDENTS: Wisconsin law provides that no
                        agreement, unilateral statement or court decree relating to marital property
                        shall adversely affect a creditor's interest, unless prior to the time
                        credit is granted the creditor is furnished a copy of the agreement,
                        statement or decree or has actual knowledge of the adverse provision.
                    </Text>
                    <Text>
                        Patriot Act To help the government fight the funding of terrorism and money
                        laundering activities, federal law requires all financial institutions to
                        obtain, verify, and record information that identifies each person who opens
                        an account.
                    </Text>
                </Flex>
            </Drawer.Content>
        </Drawer>
    );
};
